import { useEffect, useState } from "react";
import {
  getCustomerAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
} from "../../../../api";
import styles from "./EditAddress.module.css";

const emptyForm = {
  address_line: "",
  area: "",
  landmark: "",
  pincode: "",
  city: "",
  state: "",
  country: "",
  is_default: 0,
  address_type: "Home",
};

function AddressForm({ initial = emptyForm, onCancel, onSubmit, saving }) {
  const [form, setForm] = useState({ ...emptyForm, ...initial });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    setForm({ ...emptyForm, ...initial });
    setFormError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.address_id]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const required = ["address_line", "area", "city", "state", "country"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        setFormError("Please fill in all required fields.");
        return;
      }
    }
    setFormError("");
    onSubmit(form);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {formError && <div className={styles.alertError}>{formError}</div>}

      <div className={styles.rowTwo}>
        <div className={styles.field}>
          <label>
            Address Line <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            value={form.address_line}
            onChange={(e) => updateField("address_line", e.target.value)}
            placeholder="House / Flat / Street"
          />
        </div>
        <div className={styles.field}>
          <label>
            Area <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            value={form.area}
            onChange={(e) => updateField("area", e.target.value)}
            placeholder="Neighborhood / Area"
          />
        </div>
      </div>

      <div className={styles.rowTwo}>
        <div className={styles.field}>
          <label>
            City <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => updateField("city", e.target.value)}
            placeholder="City"
          />
        </div>
        <div className={styles.field}>
          <label>
            State <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            value={form.state}
            onChange={(e) => updateField("state", e.target.value)}
            placeholder="State / Province"
          />
        </div>
      </div>

      <div className={styles.rowTwo}>
        <div className={styles.field}>
          <label>
            Country <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            value={form.country}
            onChange={(e) => updateField("country", e.target.value)}
            placeholder="Country"
          />
        </div>
        <div className={styles.field}>
          <label>Pincode</label>
          <input
            type="text"
            inputMode="numeric"
            value={form.pincode || ""}
            onChange={(e) => updateField("pincode", e.target.value)}
            placeholder="Pincode / Zip"
          />
        </div>
      </div>

      <div className={styles.rowTwo}>
        <div className={styles.field}>
          <label>Landmark</label>
          <input
            type="text"
            value={form.landmark || ""}
            onChange={(e) => updateField("landmark", e.target.value)}
            placeholder="Near (optional)"
          />
        </div>
        <div className={styles.field}>
          <label>Address Type</label>
          <select
            value={form.address_type || "Home"}
            onChange={(e) => updateField("address_type", e.target.value)}
          >
            <option>Home</option>
            <option>Work</option>
            <option>Other</option>
          </select>
        </div>
      </div>

      <div className={styles.inline}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={!!form.is_default}
            onChange={(e) =>
              updateField("is_default", e.target.checked ? 1 : 0)
            }
          />
          Set as default address
        </label>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={saving}>
          {saving ? "Saving…" : "Save Address"}
        </button>
      </div>
    </form>
  );
}

export default function EditAddress() {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCustomerAddresses();
      setAddresses(res.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load addresses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddNew = () => {
    setAdding(true);
    setEditId(null);
    setMsg("");
    setError("");
  };

  const handleCancel = () => {
    setAdding(false);
    setEditId(null);
  };

  const handleCreate = async (payload) => {
    try {
      setSavingId("new");
      setError("");
      setMsg("");
      await createAddress(payload);
      setAdding(false);
      setMsg("Address added.");
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Could not add address.");
    } finally {
      setSavingId(null);
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      setSavingId(id);
      setError("");
      setMsg("");
      await updateAddress(id, payload);
      setEditId(null);
      setMsg("Address updated.");
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Could not update address.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      setSavingId(id);
      setError("");
      setMsg("");
      await deleteAddress(id);
      setMsg("Address deleted.");
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Could not delete address.");
    } finally {
      setSavingId(null);
      setConfirmId(null);
    }
  };

  // Uses full update with same fields but is_default=1 (controller will unset others)
  const handleSetDefault = async (addr) => {
    const payload = {
      address_line: addr.address_line,
      area: addr.area,
      landmark: addr.landmark,
      pincode: addr.pincode,
      city: addr.city,
      state: addr.state,
      country: addr.country,
      is_default: 1,
      address_type: addr.address_type || "Home",
    };
    await handleUpdate(addr.address_id, payload);
  };

  if (loading) {
    return <div className={styles.loading}>Loading addresses…</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h2 className={styles.heading}>Your Addresses</h2>
        <button className={styles.addBtn} onClick={handleAddNew}>
          + Add New
        </button>
      </div>

      {error && <div className={styles.alertError}>{error}</div>}
      {msg && <div className={styles.alertSuccess}>{msg}</div>}

      {adding && (
        <div className={styles.card}>
          <h3 className={styles.title}>Add Address</h3>
          <AddressForm
            initial={emptyForm}
            onCancel={handleCancel}
            onSubmit={handleCreate}
            saving={savingId === "new"}
          />
        </div>
      )}

      {/* Address list */}
      <div className={styles.grid}>
        {addresses.map((addr) => {
          const isEditing = editId === addr.address_id;
          const isDefault = !!addr.is_default;

          return (
            <div key={addr.address_id} className={styles.card}>
              {!isEditing ? (
                <>
                  <div className={styles.cardHeader}>
                    <span className={styles.title}>
                      {addr.address_type || "Home"}
                    </span>
                    {isDefault && <span className={styles.badge}>Default</span>}
                  </div>

                  <p className={styles.addressLine}>
                    {addr.address_line}
                    {addr.area ? `, ${addr.area}` : ""}
                  </p>
                  <p className={styles.addressLine}>
                    {addr.city}, {addr.state}, {addr.country}
                  </p>
                  {(addr.pincode || addr.landmark) && (
                    <p className={styles.meta}>
                      {addr.pincode ? `Pincode: ${addr.pincode}` : ""}
                      {addr.pincode && addr.landmark ? " • " : ""}
                      {addr.landmark ? `Landmark: ${addr.landmark}` : ""}
                    </p>
                  )}

                  <div className={styles.actionsRow}>
                    {!isDefault && (
                      <button
                        className={styles.linkBtn}
                        onClick={() => handleSetDefault(addr)}
                        disabled={savingId === addr.address_id}
                        title="Set as default"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      className={styles.linkBtn}
                      onClick={() => {
                        setEditId(addr.address_id);
                        setAdding(false);
                        setMsg("");
                        setError("");
                      }}
                    >
                      Edit
                    </button>

                    {confirmId === addr.address_id ? (
                      <div className={styles.confirmBar}>
                        <span>Delete this address?</span>
                        <div className={styles.confirmActions}>
                          <button
                            className={styles.secondaryBtn}
                            type="button"
                            onClick={() => setConfirmId(null)}
                            disabled={savingId === addr.address_id}
                          >
                            Cancel
                          </button>
                          <button
                            className={styles.dangerBtn}
                            type="button"
                            onClick={() => handleDelete(addr.address_id)}
                            disabled={savingId === addr.address_id}
                          >
                            {savingId === addr.address_id
                              ? "Deleting…"
                              : "Delete"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className={styles.dangerLinkBtn}
                        type="button"
                        onClick={() => setConfirmId(addr.address_id)}
                        disabled={savingId === addr.address_id}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <h3 className={styles.title}>Edit Address</h3>
                  <AddressForm
                    initial={addr}
                    onCancel={handleCancel}
                    onSubmit={(payload) =>
                      handleUpdate(addr.address_id, payload)
                    }
                    saving={savingId === addr.address_id}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
